import React, { useContext } from 'react';
import { TinkLinkUrlParameter } from '@tink/config/src/TinkLinkUrlParameter';
import { LocaleCode, MarketCode } from '@tink/config/src/markets';
import {
  getValidatedLocale,
  getValidatedMarket,
  getValidReleaseToggles,
  isTrue,
  ReleaseLabelAndTestExposure,
} from './queryValidator';

export type UrlQueryState = UrlQueryParams;

export enum TinkLinkParameters {
  // Has defaults
  IS_MOBILE_SDK = 'isMobileSDK',
  IS_IFRAME = 'isIframe',
  LOCALE = 'locale',
  MARKET = 'market',
  IS_NATIVE_NAVIGATION = 'isNativeNavigation',

  // Non optional, validation will happen
  CLIENT_ID = 'clientId',
  REDIRECT_URI = 'redirectUri',

  // Optional or flow/implementation dependent
  PROVIDER_NAME = 'providerName',
  USERNAME = 'username',
  STATE = 'state',
  SCOPE = 'scope',
  AUTHORIZATION_CODE = 'authorizationCode',
  PAYMENT_REQUEST_ID = 'paymentRequestId',
  TRANSFER_ID = 'transferId',
  SESSION_ID = 'sessionId',
  CREDENTIALS_ID = 'credentialsId',
  APP_URI = 'appUri',
  PRODUCTS = 'products',

  // Internal parameters
  HANDOFF_SESSION_ID = 'handoffSessionId',
  AUTHORIZATION_TOKEN = 'authorizationToken',
  CREDENTIALS_ID_FROM_TPP = 'credentialsIdFromTPP',
  TRACKING_ID = 'trackingId',
  DEBUG = 'debug',
  RELEASE_GROUP_TOGGLES = 'releaseGroupToggles',
}

export type UrlQueryParams = {
  [TinkLinkParameters.IS_MOBILE_SDK]: boolean;
  [TinkLinkParameters.IS_IFRAME]: boolean;
  [TinkLinkParameters.LOCALE]: LocaleCode;
  [TinkLinkParameters.MARKET]: MarketCode;
  [TinkLinkParameters.IS_NATIVE_NAVIGATION]: boolean;

  [TinkLinkParameters.CLIENT_ID]: string;
  [TinkLinkParameters.REDIRECT_URI]: string;

  [TinkLinkParameters.PROVIDER_NAME]: string | undefined;
  [TinkLinkParameters.USERNAME]: string | undefined;
  [TinkLinkParameters.STATE]: string | undefined;
  [TinkLinkParameters.SCOPE]: string | undefined;
  [TinkLinkParameters.AUTHORIZATION_CODE]: string | undefined;
  [TinkLinkParameters.CREDENTIALS_ID]: string | undefined;
  [TinkLinkParameters.APP_URI]: string | undefined;
  [TinkLinkParameters.PRODUCTS]: string | undefined;

  [TinkLinkParameters.PAYMENT_REQUEST_ID]: string | undefined;
  [TinkLinkParameters.TRANSFER_ID]: string | undefined;
  [TinkLinkParameters.HANDOFF_SESSION_ID]: string | undefined;
  [TinkLinkParameters.SESSION_ID]: string | undefined;
  [TinkLinkParameters.AUTHORIZATION_TOKEN]: string | undefined;
  [TinkLinkParameters.CREDENTIALS_ID_FROM_TPP]: string | undefined;
  [TinkLinkParameters.TRACKING_ID]: string | undefined;
  [TinkLinkParameters.DEBUG]: boolean;
  [TinkLinkParameters.RELEASE_GROUP_TOGGLES]: ReleaseLabelAndTestExposure | undefined;
};

export const defaultParams: UrlQueryParams = {
  [TinkLinkParameters.IS_MOBILE_SDK]: false,
  [TinkLinkParameters.IS_IFRAME]: false,
  [TinkLinkParameters.LOCALE]: LocaleCode.EN,
  [TinkLinkParameters.MARKET]: MarketCode.SE,
  [TinkLinkParameters.IS_NATIVE_NAVIGATION]: false,

  [TinkLinkParameters.CLIENT_ID]: '',
  [TinkLinkParameters.REDIRECT_URI]: '',

  [TinkLinkParameters.PROVIDER_NAME]: undefined,
  [TinkLinkParameters.USERNAME]: undefined,
  [TinkLinkParameters.STATE]: undefined,
  [TinkLinkParameters.SCOPE]: undefined,
  [TinkLinkParameters.AUTHORIZATION_CODE]: undefined,
  [TinkLinkParameters.CREDENTIALS_ID]: undefined,
  [TinkLinkParameters.APP_URI]: undefined,
  [TinkLinkParameters.PRODUCTS]: undefined,

  [TinkLinkParameters.PAYMENT_REQUEST_ID]: undefined,
  [TinkLinkParameters.TRANSFER_ID]: undefined,
  [TinkLinkParameters.HANDOFF_SESSION_ID]: undefined,
  [TinkLinkParameters.SESSION_ID]: undefined,
  [TinkLinkParameters.AUTHORIZATION_TOKEN]: undefined,
  [TinkLinkParameters.CREDENTIALS_ID_FROM_TPP]: undefined,
  [TinkLinkParameters.TRACKING_ID]: undefined,
  [TinkLinkParameters.DEBUG]: false,
  [TinkLinkParameters.RELEASE_GROUP_TOGGLES]: undefined,
};

export const UrlQueryContext = React.createContext<UrlQueryState>(defaultParams);

export type UrlQueryProviderProps<P> = {
  search?: string;
  Provider?: React.Provider<P>;
};

export const UrlQueryProvider: React.FC<UrlQueryProviderProps<UrlQueryParams>> = ({
  children,
  search = window.location.search,
  Provider = UrlQueryContext.Provider,
}) => {
  const parsed = new URLSearchParams(search);

  return (
    <Provider
      value={{
        [TinkLinkParameters.IS_MOBILE_SDK]: isTrue(parsed.get(TinkLinkUrlParameter.MOBILESDK)),
        [TinkLinkParameters.IS_IFRAME]: isTrue(parsed.get(TinkLinkUrlParameter.IFRAME)),
        [TinkLinkParameters.LOCALE]: getValidatedLocale(parsed.get(TinkLinkUrlParameter.LOCALE)),
        [TinkLinkParameters.MARKET]: getValidatedMarket(parsed.get(TinkLinkUrlParameter.MARKET)),
        [TinkLinkParameters.IS_NATIVE_NAVIGATION]: isTrue(
          parsed.get(TinkLinkUrlParameter.IS_NATIVE_NAVIGATION)
        ),
        [TinkLinkParameters.CLIENT_ID]: parsed.get(TinkLinkUrlParameter.CLIENT_ID) || '',
        [TinkLinkParameters.REDIRECT_URI]: parsed.get(TinkLinkUrlParameter.REDIRECT_URI) || '',

        [TinkLinkParameters.PROVIDER_NAME]:
          parsed.get(TinkLinkUrlParameter.INPUT_PROVIDER) || undefined,
        [TinkLinkParameters.USERNAME]: parsed.get(TinkLinkUrlParameter.INPUT_USERNAME) || undefined,
        [TinkLinkParameters.STATE]: parsed.get(TinkLinkUrlParameter.STATE) || undefined,
        [TinkLinkParameters.SCOPE]: parsed.get(TinkLinkUrlParameter.SCOPE) || undefined,
        [TinkLinkParameters.AUTHORIZATION_CODE]:
          parsed.get(TinkLinkUrlParameter.AUTHORIZATION_CODE) || undefined,
        [TinkLinkParameters.CREDENTIALS_ID]:
          parsed.get(TinkLinkUrlParameter.USER_CREDENTIALS_ID) || undefined,
        [TinkLinkParameters.APP_URI]: parsed.get(TinkLinkUrlParameter.APP_URI) || undefined,
        [TinkLinkParameters.PRODUCTS]: parsed.get(TinkLinkUrlParameter.PRODUCTS) || undefined,

        [TinkLinkParameters.PAYMENT_REQUEST_ID]:
          parsed.get(TinkLinkUrlParameter.PAYMENT_REQUEST_ID) || undefined,
        [TinkLinkParameters.TRANSFER_ID]: parsed.get(TinkLinkUrlParameter.TRANSFER_ID) || undefined,
        [TinkLinkParameters.HANDOFF_SESSION_ID]:
          parsed.get(TinkLinkUrlParameter.HANDOFF_SESSION_ID) || undefined,
        [TinkLinkParameters.SESSION_ID]: parsed.get(TinkLinkUrlParameter.SESSION_ID) || undefined,
        [TinkLinkParameters.AUTHORIZATION_TOKEN]:
          parsed.get(TinkLinkUrlParameter.AUTHORIZATION_TOKEN) || undefined,
        [TinkLinkParameters.CREDENTIALS_ID_FROM_TPP]:
          parsed.get(TinkLinkUrlParameter.THIRD_PARTY_CREDENTIALS_ID) || undefined,
        [TinkLinkParameters.TRACKING_ID]: parsed.get(TinkLinkUrlParameter.TRACKING_ID) || undefined,
        [TinkLinkParameters.DEBUG]: isTrue(parsed.get(TinkLinkUrlParameter.DEBUG)),
        [TinkLinkParameters.RELEASE_GROUP_TOGGLES]: getValidReleaseToggles({
          queryParam: parsed.get(TinkLinkParameters.RELEASE_GROUP_TOGGLES),
        }),
      }}
    >
      {children}
    </Provider>
  );
};

export const useUrlQuery = () => useContext(UrlQueryContext);
